// ObjectifEditor.jsx - Version corrigée pour le curseur

import React, { useState, useEffect, useRef } from 'react';
import { 
  Target, 
  Edit, 
  Save, 
  CheckCircle, 
  AlertCircle,
  Eye,
  EyeOff,
  RotateCcw,
  Lightbulb,
  Clock,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

/**
 * Composant ObjectifEditor simplifié avec historique des versions
 */
const ObjectifEditor = ({ 
  objectif, 
  onSave, 
  isReadOnly = false,
  lastUpdate = null,
  conversationData = {},
  onConversationUpdate = () => {},
  competenceId
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(objectif || '');
  const [showHistory, setShowHistory] = useState(false);
  const [objectiveHistory, setObjectiveHistory] = useState([]);
  const [shouldFocusAndPosition, setShouldFocusAndPosition] = useState(false);

  const textareaRef = useRef(null);
  const historyLoadedRef = useRef(false);

  // ✅ SIMPLIFICATION: Charger l'historique UNE SEULE FOIS depuis Firebase au montage
  useEffect(() => {
    if (!historyLoadedRef.current && conversationData.objectiveHistory?.length > 0) {
      console.log('📥 Chargement initial de l\'historique:', conversationData.objectiveHistory.length, 'entrées');
      setObjectiveHistory(conversationData.objectiveHistory);
      historyLoadedRef.current = true;
    }
  }, [conversationData.objectiveHistory?.length]);

  // Synchroniser editValue avec objectif
  useEffect(() => {
    if (objectif && !isEditing) {
      setEditValue(objectif);
    }
  }, [objectif, isEditing]);

  // Focus automatique en mode édition - CORRECTION ICI
  useEffect(() => {
    if (isEditing && textareaRef.current && shouldFocusAndPosition) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(editValue.length, editValue.length);
      setShouldFocusAndPosition(false); // Réinitialiser le flag
    }
  }, [isEditing, shouldFocusAndPosition]); // Retirer editValue.length des dépendances

  const handleStartEdit = () => {
    if (isReadOnly) return;
    setIsEditing(true);
    setShowHistory(false);
    setShouldFocusAndPosition(true); // Activer le focus uniquement au début de l'édition
  };

  const handleCancelEdit = () => {
    setEditValue(objectif || '');
    setIsEditing(false);
    setShowHistory(false);
    setShouldFocusAndPosition(false);
  };

  const handleSave = async () => {
    if (!editValue.trim()) return;

    const trimmedValue = editValue.trim();

    try {
      // Ajouter à l'historique AVANT d'appeler onSave pour éviter les problèmes de timing
      const lastEntry = objectiveHistory[objectiveHistory.length - 1];
      const isDifferent = !lastEntry || lastEntry.text !== trimmedValue;

      let updatedHistory = objectiveHistory;
      if (isDifferent) {
        console.log('💾 Sauvegarde manuelle - ajout à l\'historique');
        const newEntry = {
          text: trimmedValue,
          timestamp: Date.now(),
          date: new Date().toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        };

        updatedHistory = [...objectiveHistory.slice(-9), newEntry]; // Garder les 10 dernières versions max
        setObjectiveHistory(updatedHistory);
      } else {
        console.log('💾 Sauvegarde manuelle - même texte, pas d\'ajout');
      }

      // ✅ IMPORTANT: Mettre à jour l'historique dans conversationData ET Firebase AVANT d'appeler onSave
      // Cela garantit que l'historique est sauvegardé avant le remontage du composant
      await onConversationUpdate({
        objectiveHistory: updatedHistory,
        objectiveHasChangedSinceAI: conversationData.aiResponseGenerated && trimmedValue !== conversationData.originalObjectiveForAI
      });

      // Appeler onSave avec la nouvelle valeur (peut déclencher le remontage du composant)
      await onSave(trimmedValue);

      setIsEditing(false);
      setShowHistory(false);
      setShouldFocusAndPosition(false);

    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  const handleRestoreVersion = async (version) => {
    console.log('🔄 Restauration de la version:', version.text.substring(0, 50));
    setEditValue(version.text);
    if (onSave) {
      await onSave(version.text);
    }
    setShowHistory(false);
    setShouldFocusAndPosition(false);
  };

  const toggleHistory = () => {
    setShowHistory(!showHistory);
  };

  return (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 overflow-hidden shadow-sm">
      {/* En-tête harmonisé */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Target className="h-6 w-6 text-green-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-green-800">
                Mon objectif de recherche
              </h2>
              {lastUpdate && (
                <p className="text-sm text-green-600 mt-1">
                  Dernière modification : {lastUpdate}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={toggleHistory}
              className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors flex items-center"
              title="Voir l'historique des versions"
            >
              <Clock className="h-4 w-4 sm:mr-1" />
              <span className="text-sm hidden sm:inline">Historique ({Math.max(0, objectiveHistory.length - 1)})</span>
              {showHistory ? <ChevronUp className="h-4 w-4 sm:ml-1" /> : <ChevronDown className="h-4 w-4 sm:ml-1" />}
            </button>
            
            {!isReadOnly && !isEditing && (
              <button
                onClick={handleStartEdit}
                className="p-2 sm:px-3 sm:py-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors flex items-center"
                title="Modifier l'objectif"
              >
                <Edit className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Modifier</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="p-4">
        {/* Historique des versions */}
        {showHistory && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-medium text-blue-800 mb-3 flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              Historique des versions
            </h4>

            {objectiveHistory.length <= 1 ? (
              <div className="p-3 rounded-lg border border-blue-200 bg-white">
                <p className="text-sm text-gray-700">
                  Aucune version précédente. Les versions apparaîtront après vos sauvegardes et mises à jour.
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {objectiveHistory.slice().reverse().slice(1).map((version) => (
                  <div 
                    key={version.timestamp}
                    onClick={() => handleRestoreVersion(version)}
                    className="p-3 rounded-lg border border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50 transition-all cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 mr-3">
                        <p className="text-sm text-gray-800 leading-relaxed">
                          {version.text}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Version du {version.date}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Empêcher le déclenchement du clic parent
                          handleRestoreVersion(version);
                        }}
                        className="px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition-colors"
                        title="Restaurer cette version"
                      >
                        <RotateCcw className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => setShowHistory(false)}
              className="mt-3 text-xs text-blue-600 hover:text-blue-800 hover:underline"
            >
              Fermer l'historique
            </button>
          </div>
        )}

        {/* Mode édition */}
        {isEditing ? (
          <div className="space-y-4">
            {/* Zone de saisie */}
            <div>
              <textarea
                ref={textareaRef}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyPress}
                className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none transition-all"
                rows={4}
                placeholder="Décrivez votre objectif, ou votre idée simplement ..."
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-gray-500">
                  Appuyez sur Ctrl+Entrée pour sauvegarder, Échap pour annuler
                </p>
                <span className="text-xs text-gray-400">
                  {editValue.length} caractères
                </span>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={!editValue.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                <Save className="h-4 w-4 mr-2" />
                Sauvegarder
              </button>
            </div>
          </div>
        ) : (
          /* Mode affichage simplifié */
          <div className="space-y-4">
            {objectif ? (
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <div className="text-lg text-gray-800 leading-relaxed">
                  {objectif}
                </div>
              </div>
            ) : (
              <div className="p-8 text-center border-2 border-dashed border-green-300 rounded-lg bg-green-50">
                <Target className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-green-800 mb-2">
                  Définissez votre objectif
                </h3>
                <p className="text-green-700 text-sm mb-4">
                  Commencez par décrire votre idée, votre projet ou votre ambition.
                </p>
                {!isReadOnly && (
                  <button
                    onClick={handleStartEdit}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center mx-auto"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Commencer
                  </button>
                )}
              </div>
            )}

            {/* Informations sur les changements */}
            {objectiveHistory.length > 1 && (
              <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-blue-600 mr-2" />
                  <span className="text-sm text-blue-800">
                    Objectif mis à jour ({objectiveHistory.length - 1} versions précédentes)
                  </span>
                </div>
                <button
                  onClick={toggleHistory}
                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Voir l'historique
                </button>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default ObjectifEditor;
